import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  HelpCircle, 
  Search, 
  MessageCircle, 
  Mail, 
  Phone, 
  FileText, 
  Shield, 
  Brain, 
  Heart, 
  Users, 
  BookOpen, 
  Settings, 
  Lock, 
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Star,
  Clock,
  CheckCircle,
  X
} from 'lucide-react';
import SabiwabeeLogo from '../components/SabiwabeeLogo';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [expandedFaqs, setExpandedFaqs] = useState({});

  const toggleFaq = (faqId) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [faqId]: !prev[faqId]
    }));
  };

  const categories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Star,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'ai-therapy',
      title: 'AI Therapy',
      icon: Brain,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'features',
      title: 'Features & Tools',
      icon: Heart,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'community',
      title: 'Community',
      icon: Users,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    {
      id: 'account',
      title: 'Account & Settings',
      icon: Settings,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    },
    {
      id: 'privacy',
      title: 'Privacy & Security',
      icon: Lock,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: AlertTriangle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    }
  ];

  const faqs = {
    'getting-started': [
      {
        id: 'gs-1',
        question: 'How do I create an account?',
        answer: 'Creating an account is easy! Click the "Get Started" button on our homepage, fill in your basic information (name, email, password), and verify your email address. You can start using our free features immediately after verification.'
      },
      {
        id: 'gs-2',
        question: 'Is Sabiwabee free to use?',
        answer: 'Sabiwabee offers both free and premium features. You can access basic AI therapy conversations, mood tracking, and community forums for free. Premium features include advanced assessments, unlimited journaling, and priority support.'
      },
      {
        id: 'gs-3',
        question: 'What age do I need to be to use Sabiwabee?',
        answer: 'You must be at least 13 years old to use Sabiwabee. If you are under 18, you may need parental consent depending on your location. We recommend consulting with a parent or guardian before using our services.'
      },
      {
        id: 'gs-4',
        question: 'How do I get started with my first AI therapy session?',
        answer: 'After creating your account, navigate to the "Chat" section and select an AI therapist that matches your needs. You can start a conversation immediately - our AI is available 24/7 and will guide you through your first session.'
      }
    ],
    'ai-therapy': [
      {
        id: 'ai-1',
        question: 'How does AI therapy work?',
        answer: 'Our AI therapists use advanced natural language processing and therapeutic techniques to provide personalized mental health support. They can engage in conversations, provide coping strategies, and help you track your progress over time.'
      },
      {
        id: 'ai-2',
        question: 'Is AI therapy as effective as human therapy?',
        answer: 'AI therapy is designed to complement, not replace, human therapy. It can provide valuable support, coping strategies, and 24/7 availability, but for serious mental health concerns, we always recommend consulting with licensed human therapists.'
      },
      {
        id: 'ai-3',
        question: 'How do I know when to switch to a human therapist?',
        answer: 'Our AI system is designed to recognize when you might benefit from human support. It will suggest handover to a human therapist for crisis situations, complex issues, or when you express a preference for human interaction.'
      },
      {
        id: 'ai-4',
        question: 'Are my conversations with AI therapists private?',
        answer: 'Yes, your conversations with AI therapists are completely private and encrypted. We follow strict privacy protocols and HIPAA guidelines to ensure your mental health information remains confidential.'
      }
    ],
    'features': [
      {
        id: 'feat-1',
        question: 'How do I use the journaling feature?',
        answer: 'Navigate to the "Journal" section in your dashboard. You can write freely about your thoughts and feelings. Our AI will provide insights and patterns based on your entries, helping you understand your mental health journey better.'
      },
      {
        id: 'feat-2',
        question: 'What are mental health assessments?',
        answer: 'Mental health assessments are scientifically validated questionnaires that help evaluate your current mental health status. They cover areas like anxiety, depression, stress, and more. Results include personalized recommendations and progress tracking.'
      },
      {
        id: 'feat-3',
        question: 'How do meditation sessions work?',
        answer: 'Our meditation feature offers guided sessions ranging from 5 minutes to 30 minutes. Choose from different types like stress relief, sleep, anxiety, or general mindfulness. Track your meditation progress and build a consistent practice.'
      },
      {
        id: 'feat-4',
        question: 'Can I track my progress over time?',
        answer: 'Yes! Our progress tracking feature shows your mood trends, assessment results, meditation consistency, and overall mental health journey. You can view detailed analytics and insights to understand your patterns and improvements.'
      }
    ],
    'community': [
      {
        id: 'comm-1',
        question: 'How do I join community forums?',
        answer: 'Navigate to the "Peer Support" section to access our community forums. You can browse different topics, join discussions, and connect with others on similar mental health journeys. All forums are moderated for safety.'
      },
      {
        id: 'comm-2',
        question: 'Are community forums safe and moderated?',
        answer: 'Yes, all our community forums are actively moderated by our team. We have strict guidelines to ensure a supportive and safe environment. Users can report inappropriate content, and moderators review all posts.'
      },
      {
        id: 'comm-3',
        question: 'Can I remain anonymous in the community?',
        answer: 'You can choose how much personal information to share in the community. While your username is visible, you control what personal details you disclose. We encourage respectful and supportive interactions.'
      },
      {
        id: 'comm-4',
        question: 'How do I report inappropriate behavior?',
        answer: 'You can report inappropriate content or behavior by clicking the "Report" button on any post or comment. Our moderation team will review reports promptly and take appropriate action to maintain community safety.'
      }
    ],
    'account': [
      {
        id: 'acc-1',
        question: 'How do I change my password?',
        answer: 'Go to your Profile settings, click on "Security," and select "Change Password." You\'ll need to enter your current password and then create a new one. Make sure to use a strong, unique password.'
      },
      {
        id: 'acc-2',
        question: 'Can I delete my account?',
        answer: 'Yes, you can delete your account at any time. Go to Profile settings, scroll to the bottom, and click "Delete Account." Please note that this action is permanent and will remove all your data from our platform.'
      },
      {
        id: 'acc-3',
        question: 'How do I update my subscription?',
        answer: 'Navigate to the "Subscription" section in your dashboard. You can upgrade to premium, downgrade to free, or cancel your subscription at any time. Changes take effect immediately.'
      },
      {
        id: 'acc-4',
        question: 'How do I export my data?',
        answer: 'You can export your data by going to Profile settings and clicking "Export Data." This will include your journal entries, assessment results, and other personal information. The export will be sent to your email.'
      }
    ],
    'privacy': [
      {
        id: 'priv-1',
        question: 'How is my data protected?',
        answer: 'We use industry-standard encryption and follow HIPAA guidelines to protect your data. All information is stored securely, and we never sell your personal data to third parties. See our Privacy Policy for full details.'
      },
      {
        id: 'priv-2',
        question: 'Who can see my information?',
        answer: 'Only you and authorized Sabiwabee staff can access your information. We may share data with third-party service providers who help us operate our platform, but they are bound by strict confidentiality agreements.'
      },
      {
        id: 'priv-3',
        question: 'What happens to my data if I delete my account?',
        answer: 'When you delete your account, we permanently remove all your personal data from our systems within 30 days. This includes journal entries, assessment results, and conversation history.'
      },
      {
        id: 'priv-4',
        question: 'Is my information shared with healthcare providers?',
        answer: 'We only share your information with healthcare providers if you explicitly consent and request us to do so. You have full control over who has access to your mental health information.'
      }
    ],
    'troubleshooting': [
      {
        id: 'ts-1',
        question: 'The app is loading slowly. What should I do?',
        answer: 'Try refreshing the page, clearing your browser cache, or checking your internet connection. If the problem persists, try using a different browser or contact our support team for assistance.'
      },
      {
        id: 'ts-2',
        question: 'I can\'t log into my account.',
        answer: 'First, make sure you\'re using the correct email and password. If you\'ve forgotten your password, use the "Forgot Password" link to reset it. If you\'re still having issues, contact our support team.'
      },
      {
        id: 'ts-3',
        question: 'My AI therapist isn\'t responding properly.',
        answer: 'Try refreshing the chat or starting a new conversation. If the issue continues, it might be a temporary technical problem. You can also try switching to a different AI therapist or contact support for help.'
      },
      {
        id: 'ts-4',
        question: 'I\'m not receiving email notifications.',
        answer: 'Check your spam folder and make sure our email address isn\'t blocked. You can also verify your email address in your account settings. If the problem continues, contact our support team.'
      }
    ]
  };

  const quickGuides = [
    {
      title: 'First Steps Guide',
      description: 'Get started with Sabiwabee in 5 minutes',
      icon: Star,
      color: 'text-blue-600',
      link: '#first-steps'
    },
    {
      title: 'AI Therapy Basics',
      description: 'Learn how to have effective AI therapy sessions',
      icon: Brain,
      color: 'text-purple-600',
      link: '#ai-therapy-basics'
    },
    {
      title: 'Privacy & Security',
      description: 'Understanding how we protect your data',
      icon: Shield,
      color: 'text-green-600',
      link: '#privacy-security'
    },
    {
      title: 'Community Guidelines',
      description: 'How to participate safely in our community',
      icon: Users,
      color: 'text-orange-600',
      link: '#community-guidelines'
    }
  ];

  const filteredFaqs = faqs[activeCategory] || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2 text-gray-900 hover:text-blue-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                <SabiwabeeLogo className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Sabiwabee</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Help Center</h1>
          </div>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Find answers to your questions, learn how to use Sabiwabee effectively, and get the support you need.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Quick Guides */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Quick Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickGuides.map((guide, index) => {
              const Icon = guide.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  <div className={`w-10 h-10 ${guide.bgColor} rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${guide.color}`} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{guide.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{guide.description}</p>
                  <a href={guide.link} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center">
                    Learn more
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              );
            })}
          </div>
        </section>

        {/* Categories and FAQs */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-8">
              <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-2">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors flex items-center space-x-3 ${
                        activeCategory === category.id
                          ? `${category.bgColor} ${category.borderColor} border`
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${category.color}`} />
                      <span className="font-medium text-gray-900">{category.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* FAQs Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <div className="flex items-center space-x-3 mb-6">
                {(() => {
                  const activeCat = categories.find(cat => cat.id === activeCategory);
                  const Icon = activeCat?.icon;
                  return (
                    <>
                      <div className={`w-8 h-8 ${activeCat?.bgColor} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${activeCat?.color}`} />
                      </div>
                      <h2 className="text-2xl font-semibold text-gray-900">{activeCat?.title}</h2>
                    </>
                  );
                })()}
              </div>

              <div className="space-y-4">
                {filteredFaqs.map((faq) => (
                  <div key={faq.id} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full text-left p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900">{faq.question}</span>
                      {expandedFaqs[faq.id] ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedFaqs[faq.id] && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <section className="mt-12">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold mb-2">Still Need Help?</h2>
              <p className="text-blue-100">Our support team is here to help you get the most out of Sabiwabee.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">Live Chat</h3>
                <p className="text-blue-100 text-sm mb-3">Get instant help from our support team</p>
                <button className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                  Start Chat
                </button>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">Email Support</h3>
                <p className="text-blue-100 text-sm mb-3">Send us a detailed message</p>
                <a href="mailto:support@sabiwabee.com" className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-block">
                  Send Email
                </a>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">Phone Support</h3>
                <p className="text-blue-100 text-sm mb-3">Call us during business hours</p>
                <a href="tel:1-800-SABIWABEE" className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-block">
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Resources */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Additional Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="w-6 h-6 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Documentation</h3>
              </div>
              <p className="text-gray-600 mb-4">Detailed guides and tutorials for all Sabiwabee features.</p>
              <a href="#" className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                Browse Documentation
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3 mb-4">
                <Users className="w-6 h-6 text-green-600" />
                <h3 className="font-semibold text-gray-900">Community Forum</h3>
              </div>
              <p className="text-gray-600 mb-4">Connect with other users and share experiences.</p>
              <Link to="/app/peer-support" className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                Visit Community
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HelpCenter; 